/* tslint:disable */
import React, { useRef, useEffect, useCallback } from "react";
import styles from "./TNGoogleSearch.module.scss";
import { usePlacesWidget } from "react-google-autocomplete";
import Label from "../Label";
import { Input, InputRef } from "antd";
import { useController, useFormContext } from "react-hook-form";
import { formatAddress, getAddressObject } from "../../utils/findAddressObject";
import { InputFieldStatus } from "../../enums/InputFieldAttributesEnums";
import ConditionalRender from "../ConditionalRender";
import ErrorMessage from "../ErrorMessage";

interface GoogleSuggestionsProps {
  name: string;
  requiredField?: boolean;
  label?: string;
  placeholder?: string;
  isAccountInformation?: boolean;
  isPickupAddress?: string;
  isDeliveryAddress?: string;
  isPickupMaterialAddress?: string;
  isDeliveryMaterialAddress?: string;
  isPickupLocation?: string;
  isDeliveryLocation?: string;
  isJobSiteAddress?: boolean;
  isCompanyAddress?: boolean;
  isFilterField?: boolean;
  addOnAfter?: React.ReactNode;
  customSize?: string;
  suffixContent?: React.ReactNode;
  isJobLead?: boolean;
  isTrucking?: boolean;
}

const TNGoogleAddressSuggestions = ({
  name,
  label,
  requiredField,
  isAccountInformation,
  placeholder,
  addOnAfter,
  isDeliveryAddress,
  isPickupAddress,
  isPickupLocation,
  isDeliveryLocation,
  isPickupMaterialAddress,
  isDeliveryMaterialAddress,
  isJobSiteAddress,
  isCompanyAddress,
  isFilterField,
  customSize,
  suffixContent,
  isJobLead = false,
  isTrucking = false,
}: GoogleSuggestionsProps) => {
  const { ERROR } = InputFieldStatus;
  const {
    field,
    fieldState: { error: validationError },
  } = useController({ name });

  const { setValue, trigger } = useFormContext();
  const antInputRef = useRef<InputRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const repositionFrameRef = useRef<number | null>(null);
  const isInitializedRef = useRef(false);
  const country = "us";

  const { ref: antRef } = usePlacesWidget({
    // apiKey: process.env.REACT_GOOGLE_API_KEY,
    inputAutocompleteValue: "region",
    onPlaceSelected: async (place) => {
      const parseddata = getAddressObject(place?.address_components);
      const formattedAddress = formatAddress({
        name: place?.name,
        home: parseddata?.home,
        formattedAddress: place?.formatted_address,
      });
      if (isPickupAddress) {
        setValue("pickupAddress", formattedAddress);
        trigger("pickupAddress");
        setValue(
          "pickupAddressCoordinates",
          `${place.geometry?.location?.lat()}, ${
            place.geometry?.location?.lng() ?? null
          }`
        );
        trigger("pickupAddressCoordinates");
      }

      if (isDeliveryAddress) {
        const coordinates = `${place.geometry?.location?.lat()}, ${
          place.geometry?.location?.lng() ?? null
        }`;
        setValue("deliveryAddress", formattedAddress);
        trigger("deliveryAddress");
        setValue("deliveryAddressCoordinates", coordinates);
        trigger("deliveryAddressCoordinates");
      }
      if (isPickupLocation) {
        setValue("pickupLocation", formattedAddress);
        trigger("pickupLocation");
        setValue(
          "pickupLocationCoordinates",
          `${place.geometry?.location?.lat()}, ${
            place.geometry?.location?.lng() ?? null
          }`
        );
        trigger("pickupLocationCoordinates");
      }
      if (isDeliveryLocation) {
        setValue("deliveryLocation", formattedAddress);
        trigger("deliveryLocation");
        setValue(
          "deliveryLocationCoordinates",
          `${place.geometry?.location?.lat()}, ${
            place.geometry?.location?.lng() ?? null
          }`
        );
        trigger("deliveryLocationCoordinates");
      }
      if (isPickupMaterialAddress) {
        setValue("pickupMaterialAddress", formattedAddress);
        trigger("pickupMaterialAddress");
        setValue(
          "pickupMaterialAddressCoordinates",
          `${place.geometry?.location?.lat()}, ${
            place.geometry?.location?.lng() ?? null
          }`
        );
        trigger("pickupMaterialAddressCoordinates");
      }
      if (isDeliveryMaterialAddress) {
        setValue("deliveryMaterialAddress", formattedAddress);
        trigger("deliveryMaterialAddress");
        setValue(
          "deliveryMaterialAddressCoordinates",
          `${place.geometry?.location?.lat()}, ${
            place.geometry?.location?.lng() ?? null
          }`
        );
        trigger("deliveryMaterialAddressCoordinates");
      }
      if (isJobSiteAddress) {
        setValue("jobSiteAddress", formattedAddress);
        trigger("jobSiteAddress");
        setValue(
          "jobSiteAddressCoordinates",
          `${place.geometry?.location?.lat()}, ${
            place.geometry?.location?.lng() ?? null
          }`
        );
        trigger("jobSiteAddressCoordinates");
      }
      if (isCompanyAddress) {
        setValue("customerCompanyAddress", formattedAddress);
        trigger("customerCompanyAddress");
      }
      if (isFilterField) {
        setValue("addressField", formattedAddress);
        setValue("addressData", {
          lat: place?.geometry?.location?.lat(),
          lng: place?.geometry?.location?.lng(),
          address: formattedAddress,
        });
        trigger("addressField");
      }
    },

    options: {
      componentRestrictions: { country },
      fields: [
        "name",
        "address_components",
        "geometry",
        "place_id",
        "formatted_address",
        "vicinity",
      ],
      types: ["geocode", "establishment"],
    },
  });

  const repositionDropdown = useCallback(() => {
    // Cancel any pending frame
    if (repositionFrameRef.current) {
      cancelAnimationFrame(repositionFrameRef.current);
    }

    repositionFrameRef.current = requestAnimationFrame(() => {
      const inputElement = antInputRef.current?.input;
      if (!inputElement) return;

      // Only reposition if THIS input is focused
      if (document.activeElement !== inputElement) return;

      // Get all pac-containers and find the visible one
      const pacContainers = Array.from(
        document.querySelectorAll(".pac-container")
      ) as HTMLElement[];

      const visiblePacContainer = pacContainers.find(
        (container) =>
          container.style.display !== "none" &&
          window.getComputedStyle(container).display !== "none"
      );

      if (!visiblePacContainer) return;

      const inputRect = inputElement.getBoundingClientRect();
      const drawerBody = inputElement.closest(".ant-drawer-body");

      // Move to drawer once and hide initially to prevent flash
      if (
        drawerBody &&
        !drawerBody.contains(visiblePacContainer) &&
        !isTrucking
      ) {
        if (!isInitializedRef.current) {
          visiblePacContainer.style.opacity = "0";
        }
        drawerBody.appendChild(visiblePacContainer);
      }

      // Calculate and apply position
      if (drawerBody && !isTrucking) {
        const drawerRect = drawerBody.getBoundingClientRect();
        visiblePacContainer.style.position = "absolute";
        visiblePacContainer.style.left = `${
          inputRect.left - drawerRect.left
        }px`;
        visiblePacContainer.style.top = `${
          inputRect.bottom - drawerRect.top + inputRect.height + 2
        }px`;
        visiblePacContainer.style.zIndex = "1050";
      } else if (drawerBody && isTrucking) {
        visiblePacContainer.style.position = "absolute";
        visiblePacContainer.style.left = `${inputRect.left}px`;
        visiblePacContainer.style.top = `${inputRect.bottom}px`;
        visiblePacContainer.style.zIndex = "1050";
      } else {
        visiblePacContainer.style.position = "fixed";
        visiblePacContainer.style.left = `${inputRect.left}px`;
        visiblePacContainer.style.top = `${inputRect.bottom}px`;
        visiblePacContainer.style.zIndex = "1200";
      }

      visiblePacContainer.style.width = `${inputRect.width}px`;

      // Show dropdown after positioning to prevent flash
      if (!isInitializedRef.current) {
        // Use another RAF to ensure styles are applied
        requestAnimationFrame(() => {
          visiblePacContainer.style.opacity = "1";
          visiblePacContainer.style.transition = "opacity 0.1s ease-in-out";
          isInitializedRef.current = true;
        });
      }
    });
  }, [isTrucking]);

  useEffect(() => {
    const inputElement = antInputRef.current?.input;
    if (!inputElement) return;

    // Reset initialization flag when component mounts
    isInitializedRef.current = false;

    // Immediate positioning on focus to prevent initial jump
    const handleFocus = () => {
      isInitializedRef.current = false;
      // Position immediately on focus
      repositionDropdown();
      // And a few more times to catch async pac-container creation
      setTimeout(repositionDropdown, 0);
      setTimeout(repositionDropdown, 50);
      setTimeout(repositionDropdown, 100);
    };

    // Debounced scroll handler for better performance
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(repositionDropdown, 10);
    };

    const handleResize = () => {
      repositionDropdown();
    };

    // Event listeners
    inputElement.addEventListener("focus", handleFocus);
    inputElement.addEventListener("click", repositionDropdown);
    inputElement.addEventListener("input", repositionDropdown);
    window.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", handleResize, { passive: true });

    // MutationObserver to catch pac-container creation/changes
    const observer = new MutationObserver((mutations) => {
      // Only trigger if pac-container was added or its display changed
      const relevantChange = mutations.some((mutation) => {
        if (mutation.type === "childList") {
          return Array.from(mutation.addedNodes).some(
            (node) =>
              node instanceof HTMLElement &&
              node.classList.contains("pac-container")
          );
        }
        if (
          mutation.type === "attributes" &&
          mutation.target instanceof HTMLElement
        ) {
          return mutation.target.classList.contains("pac-container");
        }
        return false;
      });

      if (relevantChange) {
        repositionDropdown();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Aggressive initial polling to catch pac-container creation early
    let pollCount = 0;
    const maxPolls = 20; // 2 seconds max
    const intervalId = setInterval(() => {
      repositionDropdown();
      pollCount++;
      if (pollCount >= maxPolls) {
        clearInterval(intervalId);
      }
    }, 100);

    return () => {
      if (repositionFrameRef.current) {
        cancelAnimationFrame(repositionFrameRef.current);
      }
      clearTimeout(scrollTimeout);
      inputElement.removeEventListener("focus", handleFocus);
      inputElement.removeEventListener("click", repositionDropdown);
      inputElement.removeEventListener("input", repositionDropdown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, [repositionDropdown, isTrucking]);

  return (
    <div className={styles.InputContainer} ref={containerRef}>
      <div className={styles.wrapper}>
        <Label required={requiredField} label={label} name={name} />
      </div>
      <Input
        {...field}
        suffix={suffixContent}
        status={validationError ? ERROR : undefined}
        placeholder={placeholder}
        addonAfter={addOnAfter}
        ref={(c) => {
          antInputRef.current = c;
          if (c) antRef.current = c.input;
        }}
        size={customSize ?? "large"}
      />
      <ConditionalRender visible={validationError}>
        <div className={styles.suggestionsError}>
          <ErrorMessage message={validationError?.message} />
        </div>
      </ConditionalRender>
    </div>
  );
};

export default TNGoogleAddressSuggestions;
